import { useLayoutEffect, useState } from 'react';
import { placePopover } from './popoverPosition';

// Style for a dropdown panel (e.g. a filter list) that opens from an anchor
// element: fixed-positioned with placePopover, so it stays on screen -- shifted
// left/right near the page edges and flipped above the anchor near the bottom --
// and isn't clipped by scrolling containers. Re-placed while open on scroll,
// resize, and when the panel's content changes size.
//
//   const style = usePopoverPosition(isOpen, anchorRef, panelRef, { align: 'end' });
//   <div ref={panelRef} style={{ ...style, minWidth: 360, overflowY: 'auto' }}>
//
// options: gap (px between anchor and panel), align ('start' | 'center' | 'end'),
// maxHeight (the panel's preferred maximum height).
export default function usePopoverPosition(open, anchorRef, panelRef, { gap = 6, align = 'end', maxHeight = 520 } = {}) {
  const [placement, setPlacement] = useState(null);

  useLayoutEffect(() => {
    if (!open) {
      setPlacement(null);
      return undefined;
    }
    const place = () => {
      const anchor = anchorRef.current;
      const panel = panelRef.current;
      if (!anchor || !panel) return;
      const size = { width: panel.offsetWidth, height: Math.min(panel.scrollHeight, maxHeight) };
      const next = placePopover(anchor.getBoundingClientRect(), size, { gap, align });
      setPlacement((prev) => (
        prev && prev.top === next.top && prev.left === next.left && prev.maxHeight === next.maxHeight
          ? prev
          : next
      ));
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    const resizeObserver = new ResizeObserver(place);
    if (panelRef.current) {
      resizeObserver.observe(panelRef.current);
      // Content changes (e.g. a flag group expanding) change scrollHeight, not the
      // panel's box once it's at max height, so watch its children too.
      for (const child of panelRef.current.children) resizeObserver.observe(child);
    }
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
      resizeObserver.disconnect();
    };
  }, [open, anchorRef, panelRef, gap, align, maxHeight]);

  return {
    position: 'fixed',
    zIndex: 10050,
    margin: 0,
    top: placement?.top ?? 0,
    left: placement?.left ?? 0,
    right: 'auto',
    maxHeight: placement?.maxHeight ?? maxHeight,
    // Hidden until measured, so it never flashes in the wrong place.
    visibility: placement ? 'visible' : 'hidden',
  };
}
